import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Radio, RadioGroup, Typography } from '@mui/material';
import clsx from 'clsx';
import { useCheckoutSubscription } from 'services/payments/subscriptions/hooks';

import { ReactComponent as ErrorInfoIcon } from 'assets/icons/ErrorInfoIcon.svg';

import {
  ISubscriptionRecurrent,
  IMappedSubscriptionItem as Props,
  SUBSCRIPTION_COLOR,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_RECURRENT,
  SUBSCRIPTION_STATUS,
} from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { ContentWrapper } from 'components/_basic/ContentWrapper/ContentWrapper';
import { CustomFormControlLabel } from 'components/_basic/CustomFormControl/CustomFormControl';
import { SubscriptionDetailsList } from 'components/_basic/SubscriptionDetailsList/SubscriptionDetailsList';

import { formatDate } from 'helpers/formatDates';
import { getCancelSubscriptionMessage } from 'helpers/getCancelSubscriptionMessage';
import ToastService from 'helpers/ToastService';

import { AnnualLabel, MonthlyLabel } from './SubscriptionRadioLabels';

import styles from './SubscriptionHorizontalCard.module.scss';

export const SubscriptionHorizontalCard: FC<Props> = ({
  tariff,
  title,
  detailsList,
  isCurrentPlan,
  monthlyPayment,
  subscriptionRecurrent,
  endDate,
  subscriptionStatus,
  annualPayment = 0,
  currentSubscriptionStatus,
  color,
  nextSubscriptionPlan: nextPlan,
  nextSubscriptionRecurrent: nextRecurrent,
  isHasStandardOnLightTariff,
}) => {
  const { t } = useTranslation();

  const [recurrent, setRecurrent] = useState<ISubscriptionRecurrent>(SUBSCRIPTION_RECURRENT.ANNUALLY);

  const checkoutMutation = useCheckoutSubscription();

  const formattedDate = formatDate(endDate);
  const isCurrentSubscriptionStatusActive = currentSubscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE;
  const isNextPlanEqualsTariff = nextPlan === tariff;
  const isNextRecurrentEqualsRecurrent = nextRecurrent === recurrent;
  const isNextPlanFree = nextPlan === SUBSCRIPTION_PLAN.FREE;
  const isCurrentPlanSubscriptionCancelled =
    isCurrentPlan && subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED_MANUALLY;
  const isSubscriptionRecurrentEqualsRecurrent = subscriptionRecurrent === recurrent;
  const isSubscriptionCancelled = subscriptionStatus === SUBSCRIPTION_STATUS.CANCELLED_MANUALLY;
  const isTariffFree = tariff === SUBSCRIPTION_PLAN.FREE;
  const isNextPlan = isNextPlanEqualsTariff && isNextRecurrentEqualsRecurrent && !isCurrentSubscriptionStatusActive;
  const isRecurrentMonthly = recurrent === SUBSCRIPTION_RECURRENT.MONTHLY;

  useEffect((): void => {
    if (subscriptionRecurrent) {
      setRecurrent(subscriptionRecurrent);
    }
  }, [subscriptionRecurrent]);

  const radioChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setRecurrent(value as ISubscriptionRecurrent);
  };

  const checkoutHandler = async () => {

    const itemVariant = isRecurrentMonthly ? 'Billed monthly' : 'Billed annually'
    const tariffVariant = tariff === 'STANDARD' ? 'standard' : 'light'
    const itemId = isRecurrentMonthly ? `${tariffVariant}-monthly` :`${tariffVariant}-yearly` 

    try {
      // Add analytics tracking
      (window as any).dataLayer?.push({
        event: 'begin_checkout',
        value: isRecurrentMonthly ? monthlyPayment : annualPayment,
        currency: "USD", 
        ecommerce: {
          items: [
            {
              item_name: title.toLowerCase(),
              item_variant: itemVariant,
              item_id: itemId,
              price: isRecurrentMonthly ? monthlyPayment : annualPayment,
            },
          ],
        },
      });

      const payload = { subscriptionPlan: tariff, recurrent };
      const response = await checkoutMutation.mutateAsync(payload);

      if (response.url) {
        window.location.href = response.url;
      } else {
        ToastService.showError(t('noNavigationInformation'));
      }
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  return (
    <ContentWrapper className={clsx(color === SUBSCRIPTION_COLOR.SECONDARY && styles.activePlanWrapper)} isSection>
      <Typography variant="h4" className={clsx(styles.title, color === SUBSCRIPTION_COLOR.SECONDARY && styles.active)}>
        {title}
        {isCurrentPlan && (
          <Box component="span" className={styles.currentPlan}>
            {t('currentPlan')}
          </Box>
        )}
      </Typography>
      {isSubscriptionCancelled && !isTariffFree && (
        <Typography variant="subtitle2" className={clsx(styles.cancelledManuallyWrapper, styles.error)}>
          <ErrorInfoIcon />
          {getCancelSubscriptionMessage({
            nextPlan,
            nextRecurrent,
            currentRecurrent: subscriptionRecurrent,
            currentPlan: tariff,
            formattedDate,
          })}
        </Typography>
      )}
      {!isCurrentSubscriptionStatusActive && isNextPlanEqualsTariff && isNextPlanFree && (
        <Typography component="p" variant="subtitle2" className={styles.cancelledManuallyWrapper}>
          {/* TODO: update date to add 3 days and show it only if it is end date */}
          {t('youWillSwitchToPlan', { date: formattedDate })}
        </Typography>
      )}
      <Box className={styles.contentWrapper}>
        <Box className={styles.priceWrapper}>
          {!annualPayment ? (
            <Box className={styles.priceFree}>
              <Box component="p" className={styles.price}>
                {t('pricePerMonth', { price: 0 })}
              </Box>
            </Box>
          ) : (
            <>
              <RadioGroup className={styles.radioGroup} value={recurrent} onChange={radioChangeHandler}>
                <CustomFormControlLabel
                  color={color}
                  value={SUBSCRIPTION_RECURRENT.MONTHLY}
                  control={<Radio />}
                  label={
                    <MonthlyLabel
                      monthlyPayment={monthlyPayment}
                      isCurrentFrequency={!!subscriptionRecurrent && isRecurrentMonthly}
                      endDate={endDate}
                      isCurrentPlan={isCurrentPlan}
                      subscriptionStatus={subscriptionStatus}
                    />
                  }
                />
                <CustomFormControlLabel
                  color={color}
                  value={SUBSCRIPTION_RECURRENT.ANNUALLY}
                  control={<Radio />}
                  label={
                    <AnnualLabel
                      monthlyPayment={annualPayment}
                      endDate={endDate}
                      isCurrentPlan={isCurrentPlan}
                      isCurrentFrequency={!!subscriptionRecurrent && !isRecurrentMonthly}
                      subscriptionStatus={subscriptionStatus}
                    />
                  }
                />
              </RadioGroup>
              {((!isCurrentPlan && !isHasStandardOnLightTariff) || isCurrentPlanSubscriptionCancelled) && (
                <Button
                  color={color}
                  variant="contained"
                  isLoading={checkoutMutation.isPending}
                  onClick={checkoutHandler}
                  disabled={isNextPlan || checkoutMutation.isPending}
                >
                  {isNextPlan ? t('startsOn', { date: formattedDate }) : t('upgradeSubscription')}
                </Button>
              )}
              {((isCurrentPlan &&
                subscriptionRecurrent &&
                !isSubscriptionRecurrentEqualsRecurrent &&
                !isSubscriptionCancelled) ||
                isHasStandardOnLightTariff) && (
                <Button
                  color={color}
                  variant="outlined"
                  className={clsx(
                    color === SUBSCRIPTION_COLOR.SECONDARY
                      ? styles.switchToPlanSecondaryButton
                      : styles.switchToPlanPrimaryButton,
                  )}
                  isLoading={checkoutMutation.isPending}
                  disabled={checkoutMutation.isPending || isNextPlan}
                  onClick={checkoutHandler}
                >
                  {isNextPlan && t('startsOn', { date: formattedDate })}
                  {!isNextPlan &&
                    !isSubscriptionCancelled &&
                    t('switchTo', {
                      plan: t(isRecurrentMonthly ? 'month' : 'annual'),
                    })}
                  {!isNextPlan && isSubscriptionCancelled && t('upgradeToLight')}
                </Button>
              )}
            </>
          )}
        </Box>
        <SubscriptionDetailsList detailsList={detailsList} />
      </Box>
    </ContentWrapper>
  );
};
